import { IEnvironmentConfiguration } from '../environment.interface';

/**
 * Reduce the most commonly used environment values here
 */
export const environmentBase: IEnvironmentConfiguration = {
  production: false,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  environment_name: 'stg',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  api_url: 'http://127.0.0.1:4000',
  baseRoutePath: '',
  logging: {
    defaultLogLevel: 0x0010,
  },
  appsync: {
    production: true,
    android: {
      productionKey: process.env['AppSync:AndroidKey@prod'],
      stagingKey: process.env['AppSync:AndroidKey@stg'],
    },
    ios: {
      productionKey: process.env['AppSync:IosKey@prod'],
      stagingKey: process.env['AppSync:IosKey@stg'],
    },
    version: '0.0.1',
    serverUrl: process.env['Urls:AppSyncServerUrl'],
  },
  // SmartLook vendor SDK removed (#30070). Session capture is Ryn Replay only.
  // Ryn Replay stays off in the shared base: an app opts in from its own
  // environment file, so no app changes behavior until it does.
  //
  // `enabled` reads App Config so a pilot app can be turned on per label without
  // editing this file. It was previously a hardcoded false, which meant the only
  // way to enable an app was a code change, and only its dev environment had one.
  // Compared against the string deliberately: an absent key leaves
  // `process.env[...]` undefined, `undefined === 'true'` is false, so a missing or
  // misspelled key fails CLOSED rather than starting capture.
  //
  // This is still resolved at BUILD time by the webpack DefinePlugin, like every
  // other key here, so flipping it needs a rebuild and redeploy. It is not a
  // runtime toggle, and nobody should plan a pilot cutover expecting one.
  rynReplay: {
    enabled: process.env['RynReplay:Enabled'] === 'true',
    // WI 34576 (Tim, 2026-09-27): response bodies are recorded by default again
    // (opt-out). The ingest redacts sensitive keys and values (SSN, dates of birth,
    // tokens, pay, bank and card data) before storage, keeps bodies 14 days, and
    // drops them entirely for an app on RynReplay:BodyCaptureDisabledApps. An app
    // that should record no bodies at all sets this to '' in its own environment.
    // The HOST gate (UPS-owned hosts only) lives in the recorder's masking
    // policy and runs on the parsed URL first; this pattern only narrows by URL.
    allowedUrlsForPayloadRecord:
      process.env['RynReplay:AllowedUrlsForPayloadRecord'] ?? '^https://',
    allowedHostsForPayloadRecord:
      process.env['RynReplay:AllowedHostsForPayloadRecord'],
    maskAllInputs: true,
    // Reserved, not read (PR 3639 review): the masking policy always pins
    // blockClass to 'ryn-replay-block' regardless of this value, since every
    // template in the repo hard-codes that literal class name.
    blockClass: process.env['RynReplay:BlockClass'],
    playerBaseUrl: process.env['RynReplay:PlayerBaseUrl'],
    // Ryn_Replay WI 30111: the read API's own Entra audience. Optional and unset
    // until that registration exists; the player then keeps the shared scope.
    entraAudience: process.env['RynReplay:EntraAudience'],
  },
  microsoftClarity: {
    // HSE Toolbox
    projectId: process.env['MicrosoftClarity:ProjectId@prod'],
  },
  absorb: {
    onboardingDepartmentId: process.env['Absorb:OnboardingDepartmentId@prod'],
  },
  apiHeader: 'production',
  sockets: {
    signalR: {
      url: process.env['Urls:NotificationHub@stg'],
    },
  },
  liveUpdate: {
    url: '',
  },
  featuresSwitch: {
    enableSignalR: false,
  },
  offline: {
    enabled: true,
  },
  name: '',
  displayName: '',
  version: '',
  auth0: {
    domain: process.env['Auth0:Domain'],
    audience: process.env['Auth0:Audience'],
    callbackPath: 'callback',
    clientId: null,
  },
  entraId: {
    audience: process.env['EntraId:Audience'],
    authority: process.env['EntraId:Issuer'],
    instance: process.env['EntraId:Instance'],
    domain: process.env['EntraId:Domain'],
    tenantId: process.env['EntraId:TenantId'],
    clientId: process.env['EntraId:ClientId'],
  },
  syncfusionLicenseKey: process.env['SyncFusion:LicenseKey'],
  security: {
    allowEverything: false,
    securityOverride: false,
    securityOverrideJson: 'environments/security.json',
    apiUrl: process.env['Urls:SecurityApiAzure@stg'],
    appId: process.env['Security:AppId@web-safety'],
  },
  urls: {
    azureAPI: process.env['Urls:AzureApi@stg'],
    sparta: process.env['Urls:Sparta@stg'],
    spartaAPI: process.env['Urls:SpartaApi@stg'],
    viewpointAPI: process.env['Urls:ViewpointApi@stg'],
    packetAPI: process.env['Urls:PacketApi@stg'],
    securityAPI: process.env['Urls:SecurityApi@stg'],
    softwareSecurityAPI: process.env['Urls:SoftwareSecurityApi@stg'],
    fileManagementAPI: process.env['Urls:FileManagementApi@stg'],
    tasksAPI: process.env['Urls:TasksApi@stg'],
    safetyAPI: process.env['Urls:SafetyApi@stg'],
    ratesAPI: process.env['Urls:RatesApi@stg'],
    toolTrackerAPI: process.env['Urls:ToolTrackerApi@stg'],
    reportsServer: process.env['Urls:ReportsServer@stg'],
    dynamicAPI: process.env['Urls:DynamicApi@stg'],
    bridgeAPI: process.env['Urls:BridgeApi@stg'],
  },
  mobile: {
    auth401Redirect: '/page-personal-info',
    appStoreLink: {
      ios: 'https://apps.universalplant.com',
      android: 'https://apps.universalplant.com',
    },
  },
  host: process.env['Urls:Safety2HostUrl@stg'],

  blobStorage: {
    url: process.env['Urls:AzureBlobStorageUrl@prod'],
    container: process.env['AzureBlobStorage:ContainerName@stg'],
  },
};
